import React, { useMemo } from 'react';
import {
  buildExhibitionCityOptions,
  buildExhibitionCountryOptions,
  canonicalExhibitionCity,
  canonicalExhibitionCountry,
  citiesFromRowsForCountry,
  filterOptionsForDatalist,
  matchLocationOption,
  mergeExhibitionCityLists,
} from '../data/exhibitionLocations';
import { useExhibitionCountryCities } from '../useExhibitionCountryCities';
import AdminLocationCombobox from './AdminLocationCombobox';

/**
 * Editable country + city comboboxes for exhibition admin (type to search lists).
 */
export default function AdminExhibitionLocationFields({
  country = '',
  city = '',
  exhibitions = [],
  onCountryChange,
  onCityChange,
  cityRequired = true,
  countryRequired = true,
}) {
  const countryOptions = useMemo(
    () => buildExhibitionCountryOptions((exhibitions || []).map((row) => row.country)),
    [exhibitions],
  );

  const matchedCountry = useMemo(
    () => matchLocationOption(country, countryOptions),
    [country, countryOptions],
  );

  const { cities: apiCities, loading: citiesLoading, error: citiesError, canQuery } =
    useExhibitionCountryCities(matchedCountry);

  const baseCityOptions = useMemo(() => {
    const fromDb = citiesFromRowsForCountry(exhibitions, matchedCountry);
    return buildExhibitionCityOptions(matchedCountry, fromDb);
  }, [exhibitions, matchedCountry]);

  const citySearchOptions = useMemo(() => {
    if (apiCities.length) return mergeExhibitionCityLists(baseCityOptions, apiCities);
    return baseCityOptions;
  }, [apiCities, baseCityOptions]);

  const cityComboboxOptions = useMemo(() => {
    const query = String(city || '').trim();
    if (!query) return baseCityOptions;
    return citySearchOptions;
  }, [city, baseCityOptions, citySearchOptions]);

  const cityMatchCount = useMemo(() => {
    const query = String(city || '').trim();
    if (!query) return baseCityOptions.length;
    return filterOptionsForDatalist(citySearchOptions, query).length;
  }, [baseCityOptions.length, city, citySearchOptions]);

  const applyCountrySelection = (nextCountry) => {
    const canonical = canonicalExhibitionCountry(nextCountry);
    onCountryChange(canonical);
    if (!city) return;
    const cities = buildExhibitionCityOptions(
      canonical,
      citiesFromRowsForCountry(exhibitions, canonical),
    );
    const cityStillValid = cities.some(
      (c) => c.toLowerCase() === String(city).trim().toLowerCase(),
    );
    if (!cityStillValid && cities.length === 1) {
      onCityChange(cities[0]);
    }
  };

  const handleCountryBlur = () => {
    const matched = matchLocationOption(country, countryOptions);
    if (matched) applyCountrySelection(matched);
  };

  const handleCityBlur = () => {
    const normalized = canonicalExhibitionCity(city, matchedCountry);
    const matched = matchLocationOption(
      normalized,
      filterOptionsForDatalist(citySearchOptions, normalized),
    );
    const next = matched || normalized;
    if (next !== city) onCityChange(next);
  };

  const cityHint = (() => {
    if (!matchedCountry) {
      return 'Pick a country from the list first.';
    }
    if (citiesLoading && canQuery) {
      return `Loading cities for ${matchedCountry}…`;
    }
    if (apiCities.length > 0) {
      return `${apiCities.length.toLocaleString()} cities in ${matchedCountry} — type to search, or enter your own.`;
    }
    if (citiesError && canQuery) {
      return `Could not load full city list (${citiesError}). ${
        baseCityOptions.length > 0 ? 'Using suggested cities.' : 'Type the city name.'
      }`;
    }
    if (cityMatchCount > 0) {
      return `${cityMatchCount} ${city.trim() ? 'matching' : 'suggested'} ${
        cityMatchCount === 1 ? 'city' : 'cities'
      } — or type your own.`;
    }
    return 'Type the city name.';
  })();

  return (
    <>
      <div className="col-md-4">
        <AdminLocationCombobox
          id="show-country"
          label="Country"
          required={countryRequired}
          value={country}
          onChange={onCountryChange}
          onBlur={handleCountryBlur}
          options={countryOptions}
          placeholder="Type to search countries…"
          hint="Start typing — matching countries appear below."
        />
      </div>

      <div className="col-md-4">
        <AdminLocationCombobox
          id="show-city"
          label="City"
          required={cityRequired}
          value={city}
          onChange={onCityChange}
          onBlur={handleCityBlur}
          options={cityComboboxOptions}
          placeholder={matchedCountry ? 'Type to search cities…' : 'Select country first'}
          disabled={!matchedCountry}
          hint={cityHint}
        />
      </div>
    </>
  );
}
